import React from 'react';
import styled, { keyframes } from 'styled-components';

const AIWebinarTestimonial = () => {
  return (
    <TestimonialContainer>
      <OverlayGradient />
      <ContentWrapper>
        <HeaderSection>
          <TestimonialBadge>SUCCESS STORY</TestimonialBadge>
          <Title>My Journey: From Uncertainty to Efficiency with AI</Title>
          <Subtitle>A real story about how AI transformed our hiring and development process</Subtitle>
        </HeaderSection>

        <StorySection>
          <StoryPart>
            <StoryTitle>The Challenge</StoryTitle>
            <StoryText>
              As a CTO of a growing startup, I faced a common dilemma: we needed to expand our engineering team, but hiring and development costs were astronomical. Senior engineers were charging over $150,000 per year, and recruitment and training costs added another $30,000 per person. What's even more concerning is that while we were struggling with these costs, I noticed that forward-thinking companies had already been implementing AI solutions for over two years. The technological divide between early adopters and traditional businesses was becoming increasingly apparent.
            </StoryText>
          </StoryPart>

          <StoryPart>
            <StoryTitle>The Discovery</StoryTitle>
            <StoryText>
              During the AI webinar, I learned about automation tools that could significantly reduce our dependency on expensive hires. The idea of using IA to optimize our workflow and automate repetitive tasks seemed revolutionary. What struck me most was realizing how far behind many small businesses are in adopting these technologies. In countries like Japan and South Korea, AI adoption in small businesses is already at 40%, while in many Western countries, we're barely scratching 15%. This technological divide could have serious implications for global competitiveness in the coming years.
            </StoryText>
          </StoryPart>

          <StoryPart>
            <StoryTitle>The Implementation</StoryTitle>
            <StoryText>
              We implemented an AI system that automated 40% of our development tasks. This allowed us to:
            </StoryText>
            <BenefitList>
              <BenefitItem>
                <BenefitIcon>⚡</BenefitIcon>
                <BenefitText>Reduce development time by 60%</BenefitText>
              </BenefitItem>
              <BenefitItem>
                <BenefitIcon>💰</BenefitIcon>
                <BenefitText>Save $200,000 in hiring costs</BenefitText>
              </BenefitItem>
              <BenefitItem>
                <BenefitIcon>🚀</BenefitIcon>
                <BenefitText>Accelerate product launches by 75%</BenefitText>
              </BenefitItem>
            </BenefitList>
          </StoryPart>

          <StoryPart>
            <StoryTitle>The Results</StoryTitle>
            <StoryText>
              In six months, our productivity skyrocketed. We were able to maintain a smaller but more efficient team, and the savings allowed us to invest in other critical areas of the business. AI didn't replace our engineers; instead, it made them more effective and allowed them to focus on more strategic tasks. What's particularly concerning is that many businesses in developing countries are still hesitant to adopt these technologies, which could lead to a significant technological divide in the global market. Countries that don't embrace AI solutions risk falling behind in the global economy, as the gap between AI-powered businesses and traditional ones continues to widen.
            </StoryText>
          </StoryPart>

          <StoryPart>
            <StoryTitle>The Global Impact</StoryTitle>
            <StoryText>
              The implications of AI adoption extend far beyond individual businesses. We're seeing a clear divide between countries that are actively promoting AI integration and those that are lagging behind. In regions like Southeast Asia and parts of Africa, the lack of AI infrastructure and expertise could lead to significant economic challenges in the coming decade. BBGlobal's mission to democratize AI technology becomes increasingly crucial in this context, as bridging this technological gap is essential for global economic stability and growth.
            </StoryText>
          </StoryPart>

          <CallToAction>
            <CTATitle>Ready to Transform Your Team?</CTATitle>
            <CTAText>
              Join our next webinar and discover how you can implement similar strategies in your business.
            </CTAText>
            <CTAButton 
              href="https://calendly.com/pmteamwinners/ai-for-better-business-webinar" 
              target="_blank"
              rel="noopener noreferrer"
            >
              RESERVE YOUR SPOT
              <ArrowIcon>→</ArrowIcon>
            </CTAButton>
          </CallToAction>
        </StorySection>

        <AuthorSection>
          <AuthorInfo>
            <AuthorName>Michael Roberts</AuthorName>
            <AuthorTitle>Chief Technology Officer, TechStartup</AuthorTitle>
            <AuthorStats>
              <Stat>
                <StatValue>$200K+</StatValue>
                <StatLabel>Cost Savings</StatLabel>
              </Stat>
              <Stat>
                <StatValue>60%</StatValue>
                <StatLabel>Efficiency Improvement</StatLabel>
              </Stat>
              <Stat>
                <StatValue>75%</StatValue>
                <StatLabel>Faster to Market</StatLabel>
              </Stat>
            </AuthorStats>
          </AuthorInfo>
        </AuthorSection>
      </ContentWrapper>

      <FloatingElements>
        <FloatingCircle top="10%" left="5%" size="100px" opacity="0.03" />
        <FloatingCircle top="70%" left="15%" size="150px" opacity="0.02" />
        <FloatingCircle top="30%" right="10%" size="120px" opacity="0.03" />
        <GridLines />
      </FloatingElements>
    </TestimonialContainer>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(0, 255, 198, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 198, 0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

// Styled Components
const TestimonialContainer = styled.div`
  min-height: 100vh;
  background-color: #0a0e17;
  position: relative;
  overflow: hidden;
  padding: 100px 80px;
  
  @media (max-width: 991px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const OverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(10, 14, 23, 0.8) 0%, rgba(15, 20, 35, 0.9) 100%);
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1000px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const TestimonialBadge = styled.div`
  display: inline-block;
  background: rgba(0, 255, 198, 0.1);
  color: #00FFC6;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.9rem;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 255, 198, 0.2);
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a0a0;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
`;

const StorySection = styled.div`
  margin-bottom: 60px;
`;

const StoryPart = styled.div`
  margin-bottom: 40px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const StoryTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #00FFC6;
  margin-bottom: 20px;
`;

const StoryText = styled.p`
  font-size: 1.1rem;
  color: #e0e0e0;
  line-height: 1.8;
  margin-bottom: 20px;
`;

const BenefitList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(0, 255, 198, 0.1);
`;

const BenefitIcon = styled.span`
  font-size: 1.5rem;
  margin-right: 15px;
`;

const BenefitText = styled.span`
  color: #e0e0e0;
  font-size: 1rem;
`;

const CallToAction = styled.div`
  text-align: center;
  background: linear-gradient(135deg, rgba(15, 20, 35, 0.95), rgba(10, 14, 23, 0.95));
  border-radius: 16px;
  padding: 40px;
  margin-top: 60px;
  border: 1px solid rgba(0, 255, 198, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 15px;
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  color: #a0a0a0;
  margin-bottom: 30px;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00FFC6, #0057FF);
  color: #000;
  font-weight: 700;
  font-size: 1rem;
  padding: 18px 35px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(0, 255, 198, 0.3);
  animation: ${pulse} 2s infinite;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 255, 198, 0.4);
  }
`;

const ArrowIcon = styled.span`
  margin-left: 8px;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  
  ${CTAButton}:hover & {
    transform: translateX(5px);
  }
`;

const AuthorSection = styled.div`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const AuthorInfo = styled.div`
  text-align: center;
`;

const AuthorName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 5px;
`;

const AuthorTitle = styled.p`
  font-size: 1rem;
  color: #a0a0a0;
  margin-bottom: 20px;
`;

const AuthorStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Stat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #00FFC6;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #a0a0a0;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const FloatingCircle = styled.div`
  position: absolute;
  width: ${props => props.size};
  height: ${props => props.size};
  border-radius: 50%;
  background: rgba(0, 255, 198, ${props => props.opacity});
  top: ${props => props.top};
  left: ${props => props.left};
  animation: ${float} 6s ease-in-out infinite;
`;

const GridLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 255, 198, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 255, 198, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.3;
`;

export default AIWebinarTestimonial; 